<kanzi-ui-main-card
  [title]="'locations.ubicaciones.edit.title' | translate"
  [description]="'locations.ubicaciones.edit.description' | translate"
>
  <ng-template kTemplate="rightTopContent">
    <div class="flex gap-2">
      <p-button
        [label]="'common.cancel' | translate"
        icon="pi pi-times"
        [outlined]="true"
        severity="primary"
        (onClick)="onCancel()"
        [disabled]="isUpdating()"
      ></p-button>
      <p-button
        [label]="'common.save' | translate"
        icon="pi pi-save"
        severity="primary"
        (onClick)="onSave()"
        [loading]="isUpdating()"
        [disabled]="isLoading()"
      ></p-button>
    </div>
  </ng-template>

  <!-- Loading Skeleton -->
  @if (isLoading()) {
    <div class="grid">
      <div class="col-12 lg:col-8">
        <p-card>
          <p-skeleton height="3rem" styleClass="mb-4"></p-skeleton>
          <p-skeleton height="3rem" styleClass="mb-4"></p-skeleton>
          <p-skeleton height="3rem" styleClass="mb-4"></p-skeleton>
          <p-skeleton height="5rem"></p-skeleton>
        </p-card>
      </div>
    </div>
  }

  <!-- Form -->
  @if (!isLoading()) {
    <div class="grid">
      <div class="col-12 lg:col-8">
        <p-card>
          <div class="flex flex-column gap-4">
            <!-- Name -->
            <div class="field">
              <label for="name" class="font-semibold">{{ 'locations.ubicaciones.form.name' | translate }} *</label>
              <input
                id="name"
                type="text"
                pInputText
                class="w-full"
                [(ngModel)]="ubicacion.name"
                [placeholder]="'locations.ubicaciones.form.namePlaceholder' | translate"
                [disabled]="isUpdating()"
              />
            </div>

            <!-- Display Name -->
            <div class="field">
              <label for="display_name" class="font-semibold">{{ 'locations.ubicaciones.form.displayName' | translate }}</label>
              <input
                id="display_name"
                type="text"
                pInputText
                class="w-full"
                [(ngModel)]="ubicacion.display_name"
                [placeholder]="'locations.ubicaciones.form.displayNamePlaceholder' | translate"
                [disabled]="isUpdating()"
              />
              <small class="text-500">{{ 'locations.ubicaciones.form.displayNameHelp' | translate }}</small>
            </div>

            <!-- Type -->
            <div class="field">
              <label for="type" class="font-semibold">{{ 'locations.ubicaciones.form.type' | translate }} *</label>
              <p-dropdown
                id="type"
                [options]="locationTypes()"
                [ngModel]="ubicacion.type_id"
                (ngModelChange)="onTypeChange($event)"
                [placeholder]="'locations.ubicaciones.form.typePlaceholder' | translate"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                [loading]="isTypesLoading()"
                [disabled]="isUpdating()"
              ></p-dropdown>
            </div>

            <!-- Parent Location (TreeSelect) -->
            <div class="field">
              <label for="parent" class="font-semibold">{{ 'locations.ubicaciones.form.parent' | translate }}</label>
              @if (!selectedTypeId()) {
                <p-message 
                  severity="info" 
                  [text]="'locations.ubicaciones.form.parentSelectTypeFirst' | translate"
                  styleClass="w-full"
                ></p-message>
              } @else {
                <p-treeSelect
                  id="parent"
                  [options]="parentTree()"
                  [(ngModel)]="selectedParent"
                  [placeholder]="'locations.ubicaciones.form.parentPlaceholder' | translate"
                  class="w-full"
                  [loading]="isTreeLoading()"
                  [disabled]="isUpdating()"
                ></p-treeSelect>
                <small class="text-500">
                  @if (parentTypeId()) {
                    {{ 'locations.ubicaciones.form.parentCompatibleTypes' | translate }}
                  } @else {
                    {{ 'locations.ubicaciones.edit.changeHierarchy' | translate }}
                  }
                </small>
              }
            </div>

            <!-- External ID -->
            <div class="field">
              <label for="external_id" class="font-semibold">{{ 'locations.ubicaciones.form.externalId' | translate }}</label>
              <input
                id="external_id"
                type="text"
                pInputText
                class="w-full"
                [(ngModel)]="ubicacion.external_id"
                [placeholder]="'locations.ubicaciones.form.externalIdPlaceholder' | translate"
                [disabled]="isUpdating()"
              />
            </div>

            <!-- Description -->
            <div class="field">
              <label for="description" class="font-semibold">{{ 'locations.ubicaciones.form.description' | translate }}</label>
              <textarea
                id="description"
                pInputTextarea
                rows="3"
                class="w-full"
                [(ngModel)]="ubicacion.description"
                [placeholder]="'locations.ubicaciones.form.descriptionPlaceholder' | translate"
                [disabled]="isUpdating()"
              ></textarea>
            </div>

            <!-- Zone Type (opcional) -->
            <div class="field">
              <label for="zone_type" class="font-semibold">
                {{ 'locations.ubicaciones.form.zoneType' | translate }}
              </label>
              <p-dropdown
                id="zone_type"
                [options]="zoneTypeOptions"
                [(ngModel)]="ubicacion.zone_type"
                [placeholder]="'locations.ubicaciones.form.zoneTypePlaceholder' | translate"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                [showClear]="true"
                [disabled]="isUpdating()"
              ></p-dropdown>
              <small class="text-500">{{ 'locations.ubicaciones.form.zoneTypeHelp' | translate }}</small>
            </div>

            <!-- Customer Multi-Select (shown when logged user's customer has no parent) -->
            @if (showCustomerSelector()) {
              <div class="field">
                <label for="customer_ids" class="font-semibold">
                  {{ 'warehouses.form.customers' | translate }}
                  <span class="text-red-500">*</span>
                </label>
                <p-multiSelect
                  inputId="customer_ids"
                  [(ngModel)]="selectedCustomerIds"
                  [options]="customersQuery.data()?.results || []"
                  optionLabel="name"
                  optionValue="id"
                  [placeholder]="'warehouses.form.customersPlaceholder' | translate"
                  [filter]="true"
                  filterBy="name"
                  [loading]="customersQuery.isLoading()"
                  display="chip"
                  name="customer_ids"
                ></p-multiSelect>
              </div>
            }

            <!-- Coordinates -->
            <div class="grid">
              <div class="col-6">
                <div class="field">
                  <label for="longitude" class="font-semibold">{{ 'locations.ubicaciones.form.longitude' | translate }}</label>
                  <input
                    id="longitude"
                    type="number"
                    pInputText
                    class="w-full"
                    [(ngModel)]="ubicacion.longitude"
                    placeholder="-74.123456"
                    [disabled]="isUpdating()"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="field">
                  <label for="latitude" class="font-semibold">{{ 'locations.ubicaciones.form.latitude' | translate }}</label>
                  <input
                    id="latitude"
                    type="number"
                    pInputText
                    class="w-full"
                    [(ngModel)]="ubicacion.latitude"
                    placeholder="4.123456"
                    [disabled]="isUpdating()"
                  />
                </div>
              </div>
            </div>
          </div>
        </p-card>
      </div>

      <!-- Info Section -->
      <div class="col-12 lg:col-4">
        <p-card [header]="'locations.ubicaciones.edit.infoTitle' | translate">
          <p class="text-sm text-600">
            {{ 'locations.ubicaciones.edit.infoDescription' | translate }}
          </p>
        </p-card>

        @if (updateMutation.isError()) {
          <p-message
            severity="error"
            [text]="updateMutation.error().message || ('locations.ubicaciones.errors.updateError' | translate)"
            styleClass="mt-3"
          ></p-message>
        }
      </div>
    </div>
  }

  <!-- Error State -->
  @if (ubicacionQuery.isError()) {
    <p-message
      severity="error"
      [text]="'locations.ubicaciones.errors.loadError' | translate"
    ></p-message>
  }
</kanzi-ui-main-card>
